import { useLayoutEffect, useEffect, useRef, useCallback, useMemo, useState } from 'react'
import { useTimelineStore, selectTimeline } from '@/lib/store/timeline-store'
import { useAuthStore } from '@/lib/store/auth-store'
import { MessageBubble } from './MessageBubble'
import { SystemEvent } from './SystemEvent'
import { DateSeparator } from './DateSeparator'
import { shouldShowDateSeparator } from '@/lib/utils/time'
import { requireClient, getClient } from '@/lib/matrix/client'
import { serializeEvent } from '@/lib/matrix/events'
import { useReceiptStore, selectReceiptVersion } from '@/lib/store/receipt-store'
import type { Reader } from './ReadReceipts'
import type { SerializedEvent } from '@/lib/matrix/events'

interface TimelineProps {
  roomId: string
  aiEnabled?: boolean
  onReply: (event: SerializedEvent) => void
  onEdit: (event: SerializedEvent) => void
  onReact: (event: SerializedEvent) => void
  onOpenThread: (event: SerializedEvent) => void
}

const scrollStore = new Map<string, number>()

function isThreadReply(e: SerializedEvent): boolean {
  const relatesTo = e.content['m.relates_to'] as Record<string, unknown> | undefined
  return relatesTo?.rel_type === 'm.thread'
}

export function Timeline({ roomId, aiEnabled, onReply, onEdit, onReact, onOpenThread }: TimelineProps) {
  const events = useTimelineStore(selectTimeline(roomId))
  const prependEvents = useTimelineStore((s) => s.prependEvents)
  const userId = useAuthStore((s) => s.userId)
  const bottomRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const isPaginatingRef = useRef(false)
  const lastEventIdRef = useRef<string | null>(null)
  const suppressSaveRef = useRef(false)
  const stickyBottomRef = useRef(true)
  const [showJumpToBottom, setShowJumpToBottom] = useState(false)
  const prevRoomIdRef = useRef(roomId)
  const needsRestoreRef = useRef(false)

  const SYSTEM_TYPES = new Set(['m.room.member', 'm.room.name', 'm.room.topic', 'm.room.avatar', 'm.room.encryption', 'm.room.power_levels'])

  const messageEvents = events.filter(
    (e) =>
      // Bridges (mautrix/beeper) flag portal-provisioning state changes — room
      // name/avatar/topic set on creation — as not-for-display. Honor that.
      e.content['com.beeper.exclude_from_timeline'] !== true &&
      (e.type === 'm.room.message' ||
        e.type === 'm.room.encrypted' ||
        e.type === 'm.sticker' ||
        e.type === 'm.reaction' ||
        SYSTEM_TYPES.has(e.type))
  )

  const { displayEvents, lastDisplayEvent } = useMemo(() => {
    const edits = new Map<string, typeof messageEvents[0]>()
    const editEventIds = new Set<string>()

    for (const e of messageEvents) {
      const relatesTo = e.content['m.relates_to'] as Record<string, unknown> | undefined
      if (relatesTo?.rel_type === 'm.replace' && typeof relatesTo.event_id === 'string') {
        const existing = edits.get(relatesTo.event_id)
        if (!existing || e.timestamp > existing.timestamp) {
          edits.set(relatesTo.event_id, e)
        }
        editEventIds.add(e.eventId)
      }
    }

    const processed = messageEvents
      .filter((e) => !editEventIds.has(e.eventId) && e.type !== 'm.reaction' && !isThreadReply(e))
      .map((e) => {
        const edit = edits.get(e.eventId)
        if (!edit) return e
        const newContent = edit.content['m.new_content'] as Record<string, unknown> | undefined
        if (!newContent) return e
        return { ...e, content: { ...newContent, 'm.new_content': true }, senderName: e.senderName, senderAvatar: e.senderAvatar }
      })

    return { displayEvents: processed, lastDisplayEvent: processed[processed.length - 1] }
  }, [messageEvents])

  // Read receipts: show each member's avatar on the last message they've read, so
  // you can see who's caught up. A reader's marker can point at an event we don't
  // render (reaction, redaction, edit), so place by timestamp on the last visible
  // message at/before their read time. We skip the reader's own messages (never
  // "X read their own message") and never show our own receipt.
  const receiptVersion = useReceiptStore(selectReceiptVersion(roomId))
  const receiptsByEventId = useMemo(() => {
    const map = new Map<string, Reader[]>()
    const client = getClient()
    const room = client?.getRoom(roomId)
    if (!client || !room) return map
    const me = client.getUserId()

    for (const member of room.getJoinedMembers()) {
      if (member.userId === me) continue
      const rr = room.getReadReceiptForUserId(member.userId)
      if (!rr) continue
      const ts = rr.data?.ts ?? 0
      let target: SerializedEvent | undefined
      for (let i = displayEvents.length - 1; i >= 0; i--) {
        const e = displayEvents[i]!
        if (SYSTEM_TYPES.has(e.type) || e.sender === member.userId) continue
        if (e.timestamp <= ts) {
          target = e
          break
        }
      }
      if (!target) continue
      const arr = map.get(target.eventId) ?? []
      arr.push({
        userId: member.userId,
        name: member.name ?? member.userId,
        mxc: member.getMxcAvatarUrl() ?? null,
      })
      map.set(target.eventId, arr)
    }
    return map
    // receiptVersion is the recompute trigger; intentionally in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, displayEvents, receiptVersion])

  // Capture scroll position DURING RENDER, before React commits new DOM.
  // This is the only moment where containerRef still shows the old room's content.
  if (prevRoomIdRef.current !== roomId) {
    const el = containerRef.current
    if (el) {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
      if (atBottom) {
        scrollStore.set(prevRoomIdRef.current, -1)
      } else {
        scrollStore.set(prevRoomIdRef.current, el.scrollTop)
      }
    }
    prevRoomIdRef.current = roomId
    needsRestoreRef.current = true
  }

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  // Restore after DOM commit
  useLayoutEffect(() => {
    if (!needsRestoreRef.current) return
    // Don't consume the restore flag until there's actually something to restore
    // against. A room switch often commits one empty frame before the timeline
    // store populates; consuming it here would skip the restore for that room and
    // leave stickyBottomRef stuck at the previous room's value (often false), so
    // the view never scrolls to the bottom.
    const el = containerRef.current
    if (!el || displayEvents.length === 0) return
    needsRestoreRef.current = false

    suppressSaveRef.current = true
    lastEventIdRef.current = lastDisplayEvent?.eventId ?? null

    const saved = scrollStore.get(roomId)
    if (saved === undefined || saved === -1) {
      stickyBottomRef.current = true
      el.scrollTop = el.scrollHeight
      // Re-pin across the next few frames: read receipts, reactions, and the
      // placeholder→media swap can grow the timeline just after this commit and
      // otherwise leave us parked a little above the last message.
      let frames = 0
      const repin = () => {
        if (!stickyBottomRef.current || isPaginatingRef.current) return
        el.scrollTop = el.scrollHeight
        if (++frames < 8) requestAnimationFrame(repin)
      }
      requestAnimationFrame(repin)
    } else {
      stickyBottomRef.current = false
      el.scrollTop = saved
    }

    requestAnimationFrame(() => { suppressSaveRef.current = false })
  })

  // Auto-scroll for new messages when sticky
  useLayoutEffect(() => {
    if (!lastDisplayEvent || needsRestoreRef.current) return
    if (lastDisplayEvent.eventId === lastEventIdRef.current) return

    lastEventIdRef.current = lastDisplayEvent.eventId
    if (stickyBottomRef.current) {
      scrollToBottom()
    }
  })

  // On open, backfill history so a room whose initial sync only returned a few
  // recent events doesn't look like it "just started today" (and so replies to
  // older messages resolve instead of showing "Original message not loaded").
  // Only when pinned to the bottom — if there's a restored scroll position the
  // user has been here and history is already loaded; don't disturb it.
  useEffect(() => {
    let cancelled = false
    const client = getClient()
    const room = client?.getRoom(roomId)
    if (!client || !room) return

    const countMsgs = () =>
      room.getLiveTimeline().getEvents().filter((e) => {
        const t = e.getType()
        return t === 'm.room.message' || t === 'm.room.encrypted' || t === 'm.sticker'
      }).length

    ;(async () => {
      // Let the open-time scroll restore settle first.
      await new Promise((r) => requestAnimationFrame(r))
      if (cancelled || !stickyBottomRef.current) return

      for (let i = 0; i < 4 && !cancelled; i++) {
        if (countMsgs() >= 40) break
        const before = room.getLiveTimeline().getEvents().length
        try {
          await client.scrollback(room, 50)
        } catch {
          break
        }
        if (cancelled) return
        const evs = room.getLiveTimeline().getEvents()
        if (evs.length === before) break // reached the start of the room
        prependEvents(roomId, evs.map((e) => serializeEvent(e)))
        // We opened pinned to the bottom; keep it there while history fills in above.
        requestAnimationFrame(() => {
          const c = containerRef.current
          if (c && stickyBottomRef.current) c.scrollTop = c.scrollHeight
        })
      }
    })()

    return () => { cancelled = true }
  }, [roomId, prependEvents])

  // Keep the view pinned to the bottom as the content's height settles. A newly
  // received message often grows after its initial render (link previews, images,
  // decryption, layout), and the one-shot auto-scroll above can fire against a
  // stale scrollHeight, leaving the last message half-hidden behind the composer
  // until you switch rooms. Re-pinning on every size change fixes that.
  useEffect(() => {
    const content = contentRef.current
    const el = containerRef.current
    if (!content || !el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      // Re-pin to the bottom as long as we're meant to be stuck there. Guard only
      // against pagination (where we deliberately hold an older scroll position) —
      // NOT suppressSaveRef, which is held for a frame right after a room opens,
      // exactly when late-loading content (avatars, images, previews, decryption)
      // grows the timeline. Gating on it there left the view parked "a bit up".
      if (stickyBottomRef.current && !isPaginatingRef.current) {
        el.scrollTop = el.scrollHeight
      }
    })
    ro.observe(content) // message content growing (previews, images, decryption)
    ro.observe(el)      // viewport shrinking (e.g. composer expanding)
    // Observe the composer directly too: it's a sibling outside this subtree, so
    // its growth only reaches us as a (sometimes mistimed) resize of `el`.
    // Watching it explicitly makes a multi-line composer deterministically re-pin
    // the last message instead of letting it slide under the composer.
    const composer = el.closest('main')?.querySelector('.composer-wrap')
    if (composer) ro.observe(composer)
    return () => ro.disconnect()
  }, [])

  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el || suppressSaveRef.current) return

    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
    stickyBottomRef.current = atBottom
    setShowJumpToBottom(!atBottom)

    if (el.scrollTop < 100 && !isPaginatingRef.current) {
      isPaginatingRef.current = true
      const client = requireClient()
      const room = client.getRoom(roomId)
      if (room) {
        suppressSaveRef.current = true
        const prevHeight = el.scrollHeight
        client.scrollback(room, 30).then(() => {
          const timeline = room.getLiveTimeline().getEvents()
          prependEvents(
            roomId,
            timeline.map((e) => serializeEvent(e))
          )
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight - prevHeight
            requestAnimationFrame(() => { suppressSaveRef.current = false })
            isPaginatingRef.current = false
          })
        }).catch(() => {
          isPaginatingRef.current = false
          suppressSaveRef.current = false
        })
      } else {
        isPaginatingRef.current = false
      }
    }
  }, [roomId, prependEvents])

  const jumpToEvent = useCallback((eventId: string) => {
    const el = containerRef.current
    if (!el) return
    const target = el.querySelector(`[data-event-id="${CSS.escape(eventId)}"]`) as HTMLElement | null
    if (!target) return
    stickyBottomRef.current = false
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.classList.add('highlight-pulse')
    setTimeout(() => target.classList.remove('highlight-pulse'), 2000)
  }, [])

  return (
    <div className="relative flex-1">
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-y-auto px-6 py-2 scroll"
      onScroll={handleScroll}
    >
      <div ref={contentRef} className="timeline-col space-y-0.5">
        {displayEvents.map((event, idx) => {
          const prevEvent = displayEvents[idx - 1]
          const showDate = shouldShowDateSeparator(
            event.timestamp,
            prevEvent?.timestamp
          )
          const isSystem = SYSTEM_TYPES.has(event.type)
          const isGrouped =
            !showDate && !isSystem &&
            !SYSTEM_TYPES.has(prevEvent?.type ?? '') &&
            prevEvent?.sender === event.sender &&
            event.timestamp - (prevEvent?.timestamp ?? 0) < 300000

          return (
            <div key={event.eventId} data-event-id={event.eventId}>
              {showDate && <DateSeparator timestamp={event.timestamp} />}
              {isSystem ? (
                <SystemEvent event={event} />
              ) : (
                <MessageBubble
                  event={event}
                  isOwn={event.sender === userId}
                  isGrouped={isGrouped}
                  roomId={roomId}
                  onReply={onReply}
                  onEdit={onEdit}
                  onReact={onReact}
                  onOpenThread={onOpenThread}
                  onJumpToEvent={jumpToEvent}
                  aiEnabled={aiEnabled}
                  readers={receiptsByEventId.get(event.eventId)}
                />
              )}
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>
    </div>
    {showJumpToBottom && (
      <button
        onClick={() => {
          scrollToBottom()
          stickyBottomRef.current = true
          setShowJumpToBottom(false)
        }}
        className="absolute bottom-4 right-6 p-2.5 bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-full shadow-md hover:bg-[rgb(var(--np-raised))] transition-colors animate-fade-in"
        title="Jump to latest"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--np-accent))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="7 13 12 18 17 13" />
          <polyline points="7 6 12 11 17 6" />
        </svg>
      </button>
    )}
    </div>
  )
}
